import { useState } from 'react';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Button } from '@givebutter/primitives/button';
import { NonProfitManualVerificationForm } from './NonProfitManualVerificationForm';
import * as UserEmailFormDialog from 'components/nonprofit-verification/UserEmailFormDialog';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentUser } from 'customHooks/useCurrentUser';
import { useInvalidateUserAndAccount } from 'customHooks/useInvalidateUserAndAccount';

interface ManuallyVerifyButtonProps {
    showing: boolean;
    onClick: () => void;
}

const ManuallyVerifyButton = ({ showing, onClick }: ManuallyVerifyButtonProps) => {
    return (
        <Button variant="none" color="primary" className="font-medium" onClick={onClick}>
            {showing ? 'Hide manual verification' : 'I want to manually verify my nonprofit'}
        </Button>
    );
};

const NonProfitVerificationActionRequired = () => {
    const { legalEntity } = useCurrentAccount();
    const { email: userEmail, isEmailVerified } = useCurrentUser();
    const { status_reason, domain, verification_status_reason } = legalEntity || {};
    const isDomainNotProvided = status_reason?.includes('domain_not_provided') || false;
    const emailDomainMismatch = status_reason?.includes('email_domain_mismatch') || false;

    const [isManuallyVerifying, setIsManuallyVerifying] = useState(false);

    const { mutateAsync: invalidateUserData } = useInvalidateUserAndAccount();

    const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
    const onOpenChange = (open: boolean) => {
        if (!open) {
            invalidateUserData();
        }
        setIsUpdatingEmail(open);
    };

    const handleAfterSubmit = async () => {
        await invalidateUserData();
        setIsManuallyVerifying(false);
    };

    if (verification_status_reason) {
        return (
            <AlertNotification.Root type="warning">
                <AlertNotification.Icon />
                <AlertNotification.Content>
                    <AlertNotification.Title>Action required</AlertNotification.Title>

                    <AlertNotification.Description>
                        Please refer to the most recent email sent to {userEmail} from{' '}
                        {import.meta.env.VITE_DASHBOARD_SAFETY_TEAM_EMAIL}, or contact{' '}
                        <a href={`mailto:${import.meta.env.VITE_DASHBOARD_SAFETY_TEAM_EMAIL}`}>
                            {import.meta.env.VITE_DASHBOARD_SAFETY_TEAM_EMAIL}
                        </a>{' '}
                        with any questions.
                    </AlertNotification.Description>
                </AlertNotification.Content>
            </AlertNotification.Root>
        );
    }

    return (
        <>
            {!isDomainNotProvided && !emailDomainMismatch && (
                <AlertNotification.Root type="warning">
                    <AlertNotification.Icon />
                    <AlertNotification.Content>
                        <AlertNotification.Title>
                            Issue with this EIN on GuideStar
                        </AlertNotification.Title>

                        <AlertNotification.Description>
                            Your nonprofit couldn’t be automatically verified due to an issue with
                            your EIN on GuideStar.
                        </AlertNotification.Description>
                    </AlertNotification.Content>
                </AlertNotification.Root>
            )}
            {isDomainNotProvided && (
                <AlertNotification.Root type="warning">
                    <AlertNotification.Icon />
                    <AlertNotification.Content>
                        <AlertNotification.Title>
                            No website listed on GuideStar
                        </AlertNotification.Title>

                        <AlertNotification.Description>
                            Your nonprofit couldn’t be automatically verified because there isn’t a
                            website listed on your GuideStar profile. Head to your GuideStar profile
                            to make this change. Once completed, we will continue with your account
                            verification. This process might take several business days.
                        </AlertNotification.Description>
                    </AlertNotification.Content>
                </AlertNotification.Root>
            )}

            {emailDomainMismatch && (
                <AlertNotification.Root type="warning">
                    <AlertNotification.Icon />
                    <AlertNotification.Content>
                        <AlertNotification.Title>
                            Email domain doesn't match website on GuideStar
                        </AlertNotification.Title>

                        <AlertNotification.Description>
                            Your nonprofit couldn’t be automatically verified because the email
                            domain on your Givebutter account doesn’t match the website listed on
                            your{' '}
                            {legalEntity?.guidestar_response?.public_report ? (
                                <a
                                    href={legalEntity.guidestar_response.public_report}
                                    className="text-blue-600 hover:underline"
                                >
                                    GuideStar profile
                                </a>
                            ) : (
                                'GuideStar profile'
                            )}
                            . You need to update and verify your email address with one that ends
                            with <span className="text-orange-600">@{domain}</span>.
                        </AlertNotification.Description>
                    </AlertNotification.Content>
                </AlertNotification.Root>
            )}

            {emailDomainMismatch && !isEmailVerified && (
                <AlertNotification.Root type="info">
                    <AlertNotification.Icon />
                    <AlertNotification.Content>
                        <AlertNotification.Title>Verify your new email</AlertNotification.Title>
                        <AlertNotification.Description>
                            We will check to see if the domains match once your email is verified.
                        </AlertNotification.Description>
                    </AlertNotification.Content>
                </AlertNotification.Root>
            )}

            <div className="flex flex-row items-center gap-4">
                {isDomainNotProvided && legalEntity?.guidestar_response?.public_report && (
                    <a
                        href={legalEntity.guidestar_response.public_report}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <Button variant="solid">Edit GuideStar profile</Button>
                    </a>
                )}

                {emailDomainMismatch && (
                    <UserEmailFormDialog.Root
                        key={userEmail}
                        open={isUpdatingEmail}
                        onOpenChange={onOpenChange}
                    >
                        <UserEmailFormDialog.Trigger asChild>
                            <Button variant="solid">Update email</Button>
                        </UserEmailFormDialog.Trigger>
                    </UserEmailFormDialog.Root>
                )}

                <ManuallyVerifyButton
                    showing={isManuallyVerifying}
                    onClick={() => setIsManuallyVerifying(!isManuallyVerifying)}
                />
            </div>

            {isManuallyVerifying && (
                <NonProfitManualVerificationForm afterSubmit={() => handleAfterSubmit()} />
            )}
        </>
    );
};

export { NonProfitVerificationActionRequired };
